/**
 * Shared Zod schemas for the dispatcher → driver messaging surface.
 *
 * Used by the admin send-message dialog (client-side length check) and by
 * the matching Fastify routes (`parseBody(...)`). Error `message` values are
 * stable i18n keys resolved by the caller.
 */
import { z } from 'zod';

/** Hard ceiling on a message body — a dispatcher note, not a document. */
export const DRIVER_MESSAGE_MAX_LENGTH = 1000;

/**
 * Body accepted by `POST /api/dispatcher/messages`.
 *
 * `driverIds` are Driver.id values (what the dispatcher panel already holds
 * per card). One request fans out to every listed driver so a multi-select
 * send is a single round trip and a single audit entry per recipient.
 */
export const sendDriverMessageSchema = z.object({
  driverIds: z
    .array(z.string().min(1))
    .min(1, { message: 'dispatcher.message.errors.noRecipients' })
    // Bounded so a malformed client can't fan a single request across the
    // whole driver table.
    .max(200, { message: 'dispatcher.message.errors.tooManyRecipients' }),
  body: z
    .string()
    .trim()
    .min(1, { message: 'dispatcher.message.errors.bodyRequired' })
    .max(DRIVER_MESSAGE_MAX_LENGTH, { message: 'dispatcher.message.errors.bodyTooLong' }),
});

/**
 * Body accepted by `POST /api/driver/messages/read`.
 *
 * Omitting `ids` marks the driver's entire inbox read — what opening the
 * inbox screen does. Passing ids marks just those.
 */
export const markDriverMessagesReadSchema = z.object({
  ids: z.array(z.string().min(1)).optional(),
});

export type SendDriverMessageInput = z.infer<typeof sendDriverMessageSchema>;
export type MarkDriverMessagesReadInput = z.infer<typeof markDriverMessagesReadSchema>;
